import { conditionInfo, errmsg, simpleInfo } from './info';
import { Logger, Predicate } from './types';

export function check<T = unknown>(predicate: Predicate, value: T, logger: Logger = console.warn) {
  return value;
}

function keysIn(shape: { [key: string]: any }) {
  return function (value: Object, buildMsg?: boolean) {
    for (const key of Object.keys(value)) {
      if (shape[key] === undefined) {
        return true;
      }
      return `{${errmsg(key)}:...} shouldn't be in ${errmsg(value)}`;
    }
    return null;
  };
}

export function nully(value: unknown, buildMsg?: boolean) {
  if (value == null) {
    return null;
  }
  return !buildMsg ? true : simpleInfo(nully, value);
}

export function bool(value: unknown, buildMsg?: boolean) {
  if (typeof value === 'boolean') {
    return null;
  }
  return !buildMsg ? true : simpleInfo(bool, value);
}

export function string(value: unknown, buildMsg?: boolean) {
  if (typeof value === 'string') {
    return null;
  }
  return !buildMsg ? true : simpleInfo(string, value);
}

export function number(value: unknown, buildMsg?: boolean) {
  if (Number.isFinite(value)) {
    return null;
  }
  return !buildMsg ? true : simpleInfo(number, value);
}

export function isObject(value: unknown, buildMsg?: boolean) {
  if (typeof value === 'object' && value !== null) {
    return null;
  }
  return !buildMsg ? true : `${errmsg(value)} is not an object`;
}

export function isArray(value: unknown, buildMsg?: boolean) {
  if (Array.isArray(value)) {
    return null;
  }
  return !buildMsg ? true : `${errmsg(value)} is not an array`;
}

function isTest(value: unknown, buildMsg?: boolean) {
  if (typeof value === 'function') {
    return null;
  }
  return !buildMsg ? true : `${errmsg(value)} is not a test`;
}

export function integer(value: number, buildMsg?: boolean) {
  if (Number.isSafeInteger(value)) {
    return null;
  }
  return !buildMsg ? true : simpleInfo(integer, value);
}

export function positive(value: number, buildMsg?: boolean) {
  const isNum = number(value, buildMsg);
  if (isNum !== null) {
    return isNum;
  }
  if (!(value > 0)) {
    return !buildMsg ? true : simpleInfo(positive, value);
  }
  return null;
}

export function nonNegative(value: number, buildMsg?: boolean) {
  const isNum = number(value, buildMsg);
  if (isNum !== null) {
    return isNum;
  }
  if (!(value >= 0)) {
    return !buildMsg ? true : simpleInfo(nonNegative, value);
  }
  return null;
}

export function lower(threshold: number) {
  return function (value: number, buildMsg?: boolean) {
    const isNum = number(value, buildMsg);
    if (isNum !== null) {
      return isNum;
    }
    if (!(value < threshold)) {
      return !buildMsg ? true : conditionInfo('lower', value, threshold);
    }
    return null;
  };
}

export function greater(threshold: number) {
  return function (value: number, buildMsg?: boolean) {
    const isNum = number(value, buildMsg);
    if (isNum !== null) {
      return isNum;
    }
    if (!(value > threshold)) {
      return !buildMsg ? true : conditionInfo('greater', value, threshold);
    }
    return null;
  };
}

export function leq(threshold: number) {
  return function (value: number, buildMsg?: boolean) {
    const isNum = number(value, buildMsg);
    if (isNum !== null) {
      return isNum;
    }
    if (!(value <= threshold)) {
      if (!buildMsg) {
        return true;
      }
      return conditionInfo('equal or lower', value, threshold);
    }
    return null;
  };
}

export function geq(threshold: number) {
  return function (value: number, buildMsg?: boolean) {
    const isNum = number(value, buildMsg);
    if (isNum !== null) {
      return isNum;
    }
    if (!(value >= threshold)) {
      if (!buildMsg) {
        return true;
      }
      return conditionInfo('equal or greater', value, threshold);
    }
    return null;
  };
}

export function all(...predicates: Predicate[]) {
  return function (value: unknown, buildMsg?: boolean) {
    for (const predicate of predicates) {
      const result = predicate(value, buildMsg);
      if (result !== null) {
        return result;
      }
    }
    return null;
  };
}

export function any(...predicates: Predicate[]) {
  return function (value: unknown, buildMsg?: boolean) {
    if (predicates.length === 0) {
      return !buildMsg ? true : 'no arguments - any() always fails';
    }
    const errs: (string | true)[] = [];
    for (const predicate of predicates) {
      const result = predicate(value, buildMsg);
      if (result == null) {
        return null;
      }
      if (buildMsg) {
        errs.push(result);
      }
    }
    return !buildMsg ? true : errs.join(' and ');
  };
}

export function maybe(predicate: Predicate) {
  return any(nully, predicate);
}

export function is(testValue: unknown) {
  return function (value: unknown, buildMsg?: boolean) {
    if (testValue === value) {
      return null;
    }
    return !buildMsg ? true : `${errmsg(value)} is not ${errmsg(testValue)}`;
  };
}

export function isIn(...valuesList: unknown[]) {
  const valuesSet = new Set(valuesList);
  return function (value: unknown, buildMsg?: boolean) {
    if (valuesSet.size === 0) {
      if (!buildMsg) {
        return true;
      }
      return 'no arguments - isIn() always fails';
    }
    if (valuesSet.has(value)) {
      return null;
    }
    if (!buildMsg) {
      return true;
    }
    return `${errmsg(value)} isn't equal to Checks.any of the values:
    ${errmsg([...valuesSet])}`;
  };
}

export function object(shape: { [key: string]: Predicate }) {
  return function (value: any, buildMsg?: boolean) {
    const isObj = isObject(value, buildMsg);
    if (isObj !== null) {
      return isObj;
    }
    for (const [key, predicate] of Object.entries(shape)) {
      const result = predicate(value[key], buildMsg);
      if (result !== null) {
        if (!buildMsg) {
          return true;
        }
        return `{${key}: ${result}}`;
      }
    }
    return null;
  };
}

export function objectStrict(shape: { [key: string]: Predicate }) {
  return all(object(shape), keysIn(shape));
}

export function values(predicate: Predicate) {
  return function (obj: Object, buildMsg?: boolean) {
    const isObj = isObject(obj, buildMsg);
    if (isObj !== null) {
      return isObj;
    }
    for (const [key, value] of Object.entries(obj)) {
      const result = predicate(value, buildMsg);
      if (result !== null) {
        if (!buildMsg) {
          return true;
        }
        return `{${key}: ${result}}`;
      }
    }
    return null;
  };
}

export function arrayLen(len: number) {
  check(all(nonNegative, integer), len);
  return function (arr: unknown[], buildMsg?: boolean) {
    const isArr = isArray(arr, buildMsg);
    if (isArr !== null) {
      return isArr;
    }
    const realLen = arr.length;
    if (realLen !== len) {
      if (!buildMsg) {
        return true;
      }
      return `${errmsg(arr)} has wrong length: ${realLen}, should be: ${len}`;
    }
    return null;
  };
}

export function tuple(...predicates: Predicate[]) {
  const lengthTest = arrayLen(predicates.length);
  return function (value: unknown[], buildMsg?: boolean) {
    const lenResult = lengthTest(value, buildMsg);
    if (lenResult !== null) {
      return lenResult;
    }
    for (let i = 0; i < predicates.length; i += 1) {
      const predicate = predicates[i];
      const result = predicate(value[i], buildMsg);
      if (result !== null) {
        if (!buildMsg) {
          return true;
        }
        return `[${i}: ${result}]`;
      }
    }
    return null;
  };
}

export function arrayOf(predicate: Predicate) {
  return function (value: unknown[], buildMsg?: boolean) {
    const isArr = isArray(value, buildMsg);
    if (isArr != null) {
      return isArr;
    }
    for (let i = 0; i < value.length; i += 1) {
      const result = predicate(value[i], buildMsg);
      if (result != null) {
        if (!buildMsg) {
          return true;
        }
        return `[${i}: ${result}]`;
      }
    }
    return null;
  };
}

export function debug<T = unknown>(predicate: Predicate, value: T, logger?: Logger) {
  return check<T>(predicate, value, logger);
}
